// React adapter for the shared skeleton phase machine
// (`@bitacorapro/shared/loading`). The rules — the 180 ms delay before a
// skeleton paints, the 350 ms minimum once it has — live in
// nextSkeletonPhase; this hook only owns the clock and the one timer and
// obeys the command each transition returns. The PWA's Resource class is
// the other adapter; both replay SKELETON_PHASE_VECTORS in their tests.
import {
  nextSkeletonPhase,
  SKELETON_DELAY_MS,
  SKELETON_IDLE,
  SKELETON_MIN_VISIBLE_MS,
  type SkeletonPhase,
  type SkeletonPhaseEvent,
  type SkeletonPhaseState,
} from "@bitacorapro/shared/loading";
import { useEffect, useRef, useState } from "react";
import { acquireHold } from "./holdRegistry";

export type SkeletonPhaseOptions = {
  delayMs?: number;
  minVisibleMs?: number;
};

export function useSkeletonPhase(loading: boolean, opts?: SkeletonPhaseOptions): SkeletonPhase {
  const delayMs = opts?.delayMs ?? SKELETON_DELAY_MS;
  const minVisibleMs = opts?.minVisibleMs ?? SKELETON_MIN_VISIBLE_MS;

  const [state, setState] = useState<SkeletonPhaseState>(SKELETON_IDLE);
  // The machine reads the latest state synchronously (a timer can fire
  // between renders), so the ref is the source of truth and the state
  // only mirrors it for rendering.
  const stateRef = useRef<SkeletonPhaseState>(SKELETON_IDLE);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read inside the timer callback; refreshed every render so a changed
  // option applies to the next transition.
  const timing = useRef({ delayMs, minVisibleMs });
  timing.current = { delayMs, minVisibleMs };

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const dispatch = (event: SkeletonPhaseEvent) => {
    const { delayMs: d, minVisibleMs: m } = timing.current;
    const { state: next, timer } = nextSkeletonPhase(stateRef.current, event, d, m);
    stateRef.current = next;
    setState(next);
    if (timer === "keep") return;
    clearTimer();
    if (timer === "clear") return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      dispatch({ type: timer.type, at: Date.now() });
    }, timer.inMs);
  };

  useEffect(() => {
    dispatch({ type: loading ? "start" : "settle", at: Date.now() });
    // dispatch only touches refs and a stable setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Unmount (and StrictMode's simulated one): drop the timer and rewind
  // the machine, so the re-run "start" begins a fresh run instead of
  // finding a pending phase whose timer was just cleared.
  useEffect(
    () => () => {
      clearTimer();
      stateRef.current = SKELETON_IDLE;
    },
    []
  );

  // Only the hold needs to keep design:snapshot waiting: in every other
  // phase a query is still fetching and useIsFetching() already counts.
  useEffect(() => {
    if (state.phase !== "holding") return;
    return acquireHold();
  }, [state.phase]);

  return state.phase;
}
